import React, { useEffect, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import ClearIcon from '@mui/icons-material/Clear';
import { Autocomplete, InputAdornment, Paper, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';

import Search from '../../assets/icons/SearchPrimaryColor.svg';
import theme from '../../theme';
import { PRIMARY_FONT } from '../../theme/fontConstants';

const SEARCHBAR_BACKGROUND = {
  DARK: 'dark',
  DEFAULT: 'default',
  LIGHT: 'light',
};

const FreeTextSearchFilter = ({
  disabled,
  placeholder,
  onValueChange,
  onInputChange,
  field,
  operation,
  wrapperSx,
  initialValue,
  normalizeValue,
  commitValue,
}) => {
  const initialRenderRef = useRef(true);
  const toSearchTerm = (value) => {
    const raw = value || '';
    const displayed = normalizeValue ? normalizeValue(raw) : raw;
    if (commitValue && !commitValue(displayed)) return '';
    return displayed;
  };
  const [searchTerm, setSearchTerm] = useState(() => toSearchTerm(initialValue?.value));
  const lastSentRef = useRef(commitValue ? commitValue(searchTerm) : searchTerm);
  const [, refreshInput] = useReducer((version) => version + 1, 0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }
    if (!normalizeValue) {
      if (initialValue?.value == null) {
        setSearchTerm('');
      }
      return;
    }

    const raw = initialValue?.value || '';
    const displayed = normalizeValue(raw);
    const committed = commitValue ? commitValue(displayed) : displayed;
    if (committed === lastSentRef.current) return;

    lastSentRef.current = committed;
    setSearchTerm(committed ? displayed : '');
  }, [initialValue?.value, normalizeValue, commitValue]);

  const styleForFormControl = {
    '& .MuiOutlinedInput-notchedOutline': {
      border: '0px',
    },
    '& .MuiAutocomplete-input': {
      color: theme?.button?.palette?.primary,
      fontFamily: PRIMARY_FONT['400'],
    },
    '& .MuiInputBase-input::placeholder': {
      color: theme?.button?.palette?.primary,
      fontFamily: PRIMARY_FONT['400'],
      opacity: 1,
    },

    border: `1px solid ${theme?.searchBoxForFilter?.borderColor?.focus}`,
    borderRadius: '5px',
    justifyContent: 'center',
    height: '28px',
    width: '200px',
  };

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      const displayed = normalizeValue ? normalizeValue(searchTerm || '') : searchTerm || '';
      const outgoing = commitValue ? commitValue(displayed) : displayed;
      lastSentRef.current = outgoing;
      const newVal = [
        {
          value: outgoing,
          field,
          operation,
        },
      ];
      onValueChange(newVal);
    }, 750);
    return () => clearTimeout(delayedSearch);
  }, [searchTerm, operation, field, onValueChange, normalizeValue, commitValue]);

  return (
    <FormControl sx={{ ...styleForFormControl, ...wrapperSx }}>
      <Autocomplete
        disabled={disabled}
        freeSolo
        inputValue={searchTerm}
        disableClearable
        PaperComponent={(props) => (
          <Paper
            {...props}
            style={{
              borderRadius: '16px',
            }}
            placement="auto-start"
          />
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Box
                    component="img"
                    sx={{
                      height: 16,
                      width: 16,
                    }}
                    alt="Search"
                    src={Search}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {searchTerm ? (
                    <ClearIcon
                      onClick={() => {
                        setSearchTerm('');
                        onInputChange?.('');
                      }}
                      style={{ cursor: 'pointer', width: '12px', marginLeft: '8px' }}
                    />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            onChange={(event) => {
              const rawValue = event.target.value;
              const nextValue = normalizeValue ? normalizeValue(rawValue) : rawValue;
              if (nextValue === searchTerm) {
                if (rawValue !== nextValue) {
                  refreshInput();
                }
                return;
              }
              setSearchTerm(nextValue);
              onInputChange?.(nextValue);
            }}
          />
        )}
        isOptionEqualToValue={(option, val) => option._id === val._id}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        options={[]}
      />
    </FormControl>
  );
};

FreeTextSearchFilter.propTypes = {
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  onValueChange: PropTypes.func,
  onInputChange: PropTypes.func,
  field: PropTypes.string,
  operation: PropTypes.string,
  wrapperSx: PropTypes.shape({}),
  initialValue: PropTypes.shape({ value: PropTypes.string }),
  normalizeValue: PropTypes.func,
  commitValue: PropTypes.func,
};

export { FreeTextSearchFilter, SEARCHBAR_BACKGROUND };
